import { useCallback, useEffect, useState } from "react";
import {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_PDF_TYPES,
  MAX_FILE_SIZE_IMAGE_KB,
  MAX_FILE_SIZE_PDF_KB,
  GST_EXEMPT_REGISTER_AS,
} from "./partner.constants";
import { validateFile } from "./fileValidation";
import {
  generateToken,
  verifyPanNumber,
  verifyGstNumber,
  verifyAccountNumber,
  lookupAddressByPincode,
  lookupBankInfoByIfsc,
  submitPartnerApplication,
} from "./partnerApi";

const initialFormData = {
  phoneNumber: "",
  email: "",
  panNumber: "",
  gst: "",
  panName: "",
  pinCode: "",
  streetAddress: "",
  landmark: "",
  city: "",
  state: "",
  registerAs: "",
  companyRegister: "",
  officeSetup: "",
  employeeNumber: "",
  businessVintage: "",
  bankName: "",
  account: "",
  ifsc: "",
  branch: "",
};

const fileFieldConfig = {
  registerUpload: {
    allowedTypes: ALLOWED_IMAGE_TYPES,
    maxSizeKB: MAX_FILE_SIZE_IMAGE_KB,
    typesLabel: "JPEG, PNG, and GIF image",
  },
  cancelledCheck: {
    allowedTypes: ALLOWED_IMAGE_TYPES,
    maxSizeKB: MAX_FILE_SIZE_IMAGE_KB,
    typesLabel: "JPEG, PNG, and GIF image",
  },
  kyc: {
    allowedTypes: ALLOWED_PDF_TYPES,
    maxSizeKB: MAX_FILE_SIZE_PDF_KB,
    typesLabel: "PDF",
  },
  uploadGst: {
    allowedTypes: ALLOWED_PDF_TYPES,
    maxSizeKB: MAX_FILE_SIZE_PDF_KB,
    typesLabel: "PDF",
  },
};

const initialFiles = {
  registerUpload: null,
  cancelledCheck: null,
  kyc: null,
  uploadGst: null,
};

const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export default function usePartnerForm({ onSuccess, onSubmitError }) {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState(initialFiles);
  const [fileErrors, setFileErrors] = useState({});
  const [panInvalid, setPanInvalid] = useState(false);
  const [gstInvalid, setGstInvalid] = useState(false);
  const [ifscMessage, setIfscMessage] = useState("");
  const [accountMessage, setAccountMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isGstExempt = GST_EXEMPT_REGISTER_AS.includes(formData.registerAs);

  // DSA-Individual / DSA-Without-GST don't require GST — clear any
  // previously-set errors on those fields as soon as the option is chosen.
  useEffect(() => {
    if (isGstExempt) {
      setErrors((prev) => ({ ...prev, gst: false }));
      setFileErrors((prev) => ({ ...prev, uploadGst: "" }));
    }
  }, [isGstExempt]);

  const setField = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setField(name, value);
    },
    [setField]
  );

  const clearAddressFields = () => {
    setFormData((prev) => ({ ...prev, city: "", state: "" }));
  };

  const clearGstDependentFields = () => {
    setFormData((prev) => ({
      ...prev,
      gst: "",
      pinCode: "",
      streetAddress: "",
      city: "",
      state: "",
    }));
  };

  // --- Field-specific handlers with side effects ---

  const handlePhoneChange = handleChange;
  const handleEmailChange = handleChange;

  const handlePanChange = useCallback(
    (e) => {
      const value = e.target.value.toUpperCase();
      setField("panNumber", value);

      if (value.length === 10) {
        verifyPanNumber(value)
          .then((res) => {
            if (res?.data?.data !== undefined) {
              setField("panName", res.data.data.fullName);
              setPanInvalid(false);
            } else {
              setField("panName", "");
              setField("panNumber", "");
              setField("gst", "");
              setPanInvalid(true);
            }
          })
          .catch((err) => console.log(err));
      }

      if (value.length < 10) {
        setFormData((prev) => ({
          ...prev,
          panName: "",
          gst: "",
          pinCode: "",
          streetAddress: "",
          city: "",
          state: "",
        }));
      }
    },
    [setField]
  );

  const handleGstChange = useCallback(
    (e) => {
      const value = e.target.value.toUpperCase();
      setField("gst", value);

      if (value.length === 15 && !isGstExempt) {
        verifyGstNumber(value)
          .then((res) => {
            const data = res?.data?.data;
            if (data === undefined) {
              clearGstDependentFields();
              setGstInvalid(true);
              return;
            }
            const panNameNormalized = formData.panName.replace(/ /g, "").toLowerCase();
            const legalName = (data.data?.legalNameOfBusiness || "")
              .replace(/ /g, "")
              .toLowerCase();
            const tradeName = (data.data?.tradeName || "")
              .trim()
              .replace(/ /g, "")
              .toLowerCase();

            if (panNameNormalized === legalName || panNameNormalized === tradeName) {
              setGstInvalid(false);
            } else {
              clearGstDependentFields();
              setGstInvalid(true);
            }
          })
          .catch((err) => console.log(err));
      }
    },
    [setField, formData.registerAs, formData.panName]
  );

  const handlePinCodeChange = handleChange;

  const handlePinCodeLookup = useCallback((e) => {
    const value = e.target.value;
    if (value.length !== 6) return;

    lookupAddressByPincode(value)
      .then((res) => {
        const postOffice = res?.data?.[0]?.PostOffice?.[0];
        if (postOffice) {
          setFormData((prev) => ({
            ...prev,
            state: postOffice.State,
            city: postOffice.District,
          }));
          setErrors((prev) => ({ ...prev, state: false, city: false }));
        } else {
          clearAddressFields();
        }
      })
      .catch((err) => console.log(err));
  }, []);

  const handleIfscChange = useCallback(
    (e) => {
      setField("ifsc", e.target.value);
      setIfscMessage("");
    },
    [setField]
  );

  const handleIfscBlur = useCallback((e) => {
    const value = e.target.value;
    lookupBankInfoByIfsc(value)
      .then((res) => {
        const data = res.data;
        if (data && Object.keys(data).length) {
          setIfscMessage("");
          setFormData((prev) => ({ ...prev, branch: `${data.BRANCH}, ${data.CITY}` }));
          setErrors((prev) => ({ ...prev, branch: false }));
        } else {
          setIfscMessage("Invalid IFSC code.");
          setFormData((prev) => ({ ...prev, branch: "" }));
        }
      })
      .catch(() => {
        setIfscMessage("Invalid IFSC code.");
        setFormData((prev) => ({ ...prev, branch: "" }));
      });
  }, []);

  // --- Generic file handler ---

  const handleFileChange = useCallback((fieldName) => (e) => {
    const file = e.target.files?.[0] || null;
    setFiles((prev) => ({ ...prev, [fieldName]: file }));
    setFileErrors((prev) => ({ ...prev, [fieldName]: "" }));

    const message = validateFile(file, fileFieldConfig[fieldName]);
    if (message) {
      setFileErrors((prev) => ({ ...prev, [fieldName]: message }));
    }
  }, []);

  // --- Validation ---

  const validate = useCallback(() => {
    const nextErrors = {};
    const nextFileErrors = {};

    if (!formData.email || !validateEmail(formData.email)) nextErrors.email = true;
    if (!formData.phoneNumber || formData.phoneNumber.replace(/ /g, "").length !== 10)
      nextErrors.phoneNumber = true;
    if (!formData.panNumber || formData.panNumber.length !== 10) nextErrors.panNumber = true;
    if (!formData.gst && !isGstExempt) nextErrors.gst = true;
    if (!formData.panName) nextErrors.panName = true;
    if (!formData.pinCode || formData.pinCode.length !== 6) nextErrors.pinCode = true;
    if (!formData.streetAddress) nextErrors.streetAddress = true;
    if (!formData.landmark) nextErrors.landmark = true;
    if (!formData.city) nextErrors.city = true;
    if (!formData.state) nextErrors.state = true;
    if (!formData.registerAs) nextErrors.registerAs = true;
    if (!formData.companyRegister) nextErrors.companyRegister = true;
    if (formData.companyRegister === "Yes" && !files.registerUpload)
      nextFileErrors.registerUpload = "Please fill here";
    if (!formData.officeSetup) nextErrors.officeSetup = true;
    if (!formData.employeeNumber) nextErrors.employeeNumber = true;
    if (!formData.businessVintage) nextErrors.businessVintage = true;
    if (!formData.bankName) nextErrors.bankName = true;
    if (!formData.account) nextErrors.account = true;
    if (!formData.ifsc) setIfscMessage("Please fill here");
    if (!formData.branch) nextErrors.branch = true;
    if (!files.cancelledCheck) nextFileErrors.cancelledCheck = "Please fill here";
    if (!files.kyc) nextFileErrors.kyc = "Please fill here";
    if (!files.uploadGst && !isGstExempt)
      nextFileErrors.uploadGst = "Please fill here";

    setErrors(nextErrors);
    setFileErrors((prev) => ({ ...prev, ...nextFileErrors }));

    return Object.keys(nextErrors).length === 0 && Object.keys(nextFileErrors).length === 0;
  }, [formData, files, isGstExempt]);

  // --- Submit ---

  const submit = useCallback(() => {
    setIsSubmitting(true);
    generateToken()
      .then((res) => {
        const token = res.data?.token;
        if (!token) {
          setIsSubmitting(false);
          return;
        }

        const mobileNumber = formData.phoneNumber.split(" ").join("");

        submitPartnerApplication({
          fields: {
            mobile_number: mobileNumber,
            email_id: formData.email,
            pan_card: formData.panNumber,
            gst: formData.gst,
            name: formData.panName,
            street: formData.streetAddress,
            landmark: formData.landmark,
            city: formData.city,
            state: formData.state,
            pincode: formData.pinCode,
            register_as: formData.registerAs,
            is_company_registered: formData.companyRegister,
            office_setup: formData.officeSetup,
            no_of_employees: formData.employeeNumber,
            business_vintage: formData.businessVintage,
            bank_name: formData.bankName,
            account_no: formData.account,
            ifsc: formData.ifsc,
            branch: formData.branch,
            token,
          },
          files: {
            register_upload: files.registerUpload,
            cancelled_check: files.cancelledCheck,
            kyc: files.kyc,
            upload_gst: files.uploadGst,
          },
        })
          .then((res) => {
            const response = res?.data;
            if (response?.status === 200) {
              onSuccess?.(response.message);
            } else {
              onSubmitError?.(response?.message);
            }
            setIsSubmitting(false);
          })
          .catch((err) => {
            console.log(err);
            setIsSubmitting(false);
          });
      })
      .catch((err) => {
        console.log(err);
        setIsSubmitting(false);
      });
  }, [formData, files, onSuccess, onSubmitError]);

  const handleVerifyAndSubmit = useCallback(() => {
    if (!validate()) return;

    setIsSubmitting(true);
    verifyAccountNumber({
      accountNumber: formData.account,
      accountHolderName: formData.bankName,
      ifscCode: formData.ifsc,
    })
      .then((res) => {
        if (res?.data?.data !== undefined) {
          setAccountMessage("");
          submit();
        } else {
          setIsSubmitting(false);
          setFormData((prev) => ({ ...prev, branch: "" }));
          setAccountMessage("Invalid account details");
        }
      })
      .catch((err) => console.log(err));
  }, [validate, formData.account, formData.bankName, formData.ifsc, submit]);

  return {
    formData,
    errors,
    files,
    fileErrors,
    panInvalid,
    gstInvalid,
    ifscMessage,
    accountMessage,
    isSubmitting,
    isGstExempt,
    handleChange,
    handlePhoneChange,
    handleEmailChange,
    handlePanChange,
    handleGstChange,
    handlePinCodeChange,
    handlePinCodeLookup,
    handleIfscChange,
    handleIfscBlur,
    handleFileChange,
    handleVerifyAndSubmit,
  };
}